import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, useWindowDimensions, View } from "react-native";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import VStack from "../ui_atoms/VStack";
import Carousel, { CarouselRef } from "./Carousel";
import LiftDiagramSvg from "../assets/images/lift-diagram.svg";
import { BodyMediumText, TitleLargeText } from "./FormattedText";
import IconButton from "./IconButton";
import MarkdownView from "./MarkdownView";
import HStack from "../ui_atoms/HStack";
import { Rectangle } from "./WelcomeCarousel/components/Rectangle";
import { CheckItem } from "./WelcomeCarousel/components/CheckItem";

export type ButtonPosition = {
    x: number;
    y: number;
    height: number;
    width: number;
};

type LargeWelcomeCarouselProps = {
    onCarouselChange: (toIndex: number | "full") => void;
    onButtonLayout?: (event: ButtonPosition) => void;
    autoSlider?: boolean;
    carouselScale: number;
};

type Step2ItemProps = {
    index: number;
    text: string;
};

const ARROWS_PADDING = 21;

const AUTOMATIC_SLIDESHOW_DELAY = 4000;
export const AUTOMATIC_SLIDESHOW_TRANSITION = 1000;

export const LARGE_CAROUSEL_WIDTH = 370;
export const LARGE_CAROUSEL_HEIGHT = 563;
export const BUTTON_BORDER_RADIUS = 47;

const STEP_2_CIRCLE_SIZE = 46;

const Step2Item = ({ index, text }: Step2ItemProps) => {
    const { colors } = useTheme();

    const indexPosition = [
        {},
        { containerLeft: 24, top: 13, left: 14 },
        { containerLeft: 5, top: 12, left: 15 },
        { containerLeft: 55, top: 3, left: 11 },
    ];

    return (
        <HStack
            gap="2/3"
            style={{ position: "relative", left: indexPosition[index].containerLeft }}
        >
            <>
                <View
                    style={{
                        width: STEP_2_CIRCLE_SIZE,
                        height: STEP_2_CIRCLE_SIZE,
                        borderRadius: STEP_2_CIRCLE_SIZE / 2,
                        backgroundColor: colors.accentLighter,
                    }}
                />
                <TitleLargeText
                    style={{
                        position: "absolute",
                        fontSize: 44,
                        color: colors.secondary,
                        top: indexPosition[index].top,
                        left: indexPosition[index].left,
                    }}
                    allowFontScaling={false}
                >
                    {index}
                </TitleLargeText>
            </>
            <BodyMediumText style={{ width: "70%" }} allowFontScaling={false}>
                {text}
            </BodyMediumText>
        </HStack>
    );
};

export const LargeWelcomeCarousel = ({
    onCarouselChange,
    onButtonLayout,
    autoSlider = true,
    carouselScale,
}: LargeWelcomeCarouselProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();

    const steps = useMemo(
        () => [
            <View
                key="0"
                style={[
                    styles.screen,
                    {
                        justifyContent: "center",
                        alignSelf: "center",
                        width: `${100 + (1 - carouselScale) * 100}%`,
                        transform: [{ scale: carouselScale }],
                    },
                ]}
            >
                <MarkdownView
                    allowFontScaling={false}
                    fontColor={colors.tertiary}
                    markdownStyle={{
                        heading1: { fontSize: 32, textAlign: "center" },
                        textgroup: { textAlign: "center" },
                    }}
                    markdown={i18n.t("screens.auth.presentation_carousel.step_1.title")}
                />
            </View>,
            <VStack
                gap={1.25}
                key="1"
                style={[
                    styles.screen,
                    {
                        justifyContent: "center",
                        alignSelf: "center",
                        width: `${100 + (1 - carouselScale) * 100}%`,
                        transform: [{ scale: carouselScale }],
                    },
                ]}
            >
                <Step2Item
                    index={1}
                    text={i18n.t("screens.auth.presentation_carousel.step_2.detail_1")}
                />
                <Step2Item
                    index={2}
                    text={i18n.t("screens.auth.presentation_carousel.step_2.detail_2")}
                />
                <Step2Item
                    index={3}
                    text={i18n.t("screens.auth.presentation_carousel.step_2.detail_3")}
                />
            </VStack>,
            <VStack
                gap="2/3"
                key="2"
                style={[
                    styles.screen,
                    {
                        justifyContent: "center",
                        alignItems: "center",
                        transform: [{ scale: carouselScale }],
                    },
                ]}
            >
                <Rectangle
                    icons={["hotel"]}
                    title={i18n.t("screens.auth.presentation_carousel.step_3.clients")}
                    description={
                        <BodyMediumText numberOfLines={3} allowFontScaling={false}>
                            {i18n.t(
                                "screens.auth.presentation_carousel.step_3.clients_description",
                            )}
                        </BodyMediumText>
                    }
                />
                <Rectangle
                    icons={["user-female", "user-male"]}
                    title={i18n.t("screens.auth.presentation_carousel.step_3.workers")}
                    description={
                        <BodyMediumText numberOfLines={3} allowFontScaling={false}>
                            {i18n.t(
                                "screens.auth.presentation_carousel.step_3.workers_description",
                            )}
                        </BodyMediumText>
                    }
                />
            </VStack>,
            <VStack
                gap={0.66 * carouselScale}
                key="2"
                style={[styles.screen, { justifyContent: "center", alignItems: "center" }]}
            >
                <Rectangle
                    style={{
                        transform: [{ scale: carouselScale }],
                    }}
                    description={
                        <View
                            style={{
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                flex: 1,
                            }}
                        >
                            <CheckItem
                                markdown={i18n.t(
                                    "screens.auth.presentation_carousel.step_4.detail_1",
                                )}
                            />
                            <CheckItem
                                markdown={i18n.t(
                                    "screens.auth.presentation_carousel.step_4.detail_2",
                                )}
                            />
                            <CheckItem
                                markdown={i18n.t(
                                    "screens.auth.presentation_carousel.step_4.detail_3",
                                )}
                            />
                        </View>
                    }
                />
                <LiftDiagramSvg width="100%" />
            </VStack>,
        ],
        [carouselScale, colors.tertiary, i18n, styles.screen],
    );

    const carouselRef = useRef<CarouselRef>(null);

    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const intervalRef = useRef<NodeJS.Timeout>();

    const clearAutoscrollInterval = useCallback(() => {
        clearInterval(intervalRef.current);
    }, []);

    const onPrev = useCallback(() => {
        if (canPrev) {
            carouselRef.current?.prev();
            clearAutoscrollInterval();
        }
    }, [canPrev, clearAutoscrollInterval]);

    const onNext = useCallback(() => {
        if (canNext) {
            carouselRef.current?.next();
        } else {
            // trigger slide animation
            onCarouselChange("full");
        }
        clearAutoscrollInterval();
    }, [canNext, clearAutoscrollInterval, onCarouselChange]);

    useEffect(() => {
        if (carouselRef.current && autoSlider) {
            intervalRef.current = setInterval(() => {
                carouselRef.current?.next();
            }, AUTOMATIC_SLIDESHOW_DELAY);

            return clearAutoscrollInterval;
        }
    }, [autoSlider, clearAutoscrollInterval]);

    const containerRef = useRef<View>(null);

    const { width: windowWidth, height: windowHeight } = useWindowDimensions();

    useEffect(() => {
        containerRef.current?.measure((x, y, width, height, pageX, pageY) => {
            onButtonLayout?.({ x: pageX, y: pageY, width: width, height: height });
        });
    }, [windowWidth, windowHeight, onButtonLayout]);

    // const CAROUSEL_BOTTOM_HEIGHT = LARGE_CAROUSEL_HEIGHT * carouselScale;

    return (
        <>
            <View
                ref={containerRef}
                style={{
                    height: LARGE_CAROUSEL_HEIGHT * carouselScale,
                    width: LARGE_CAROUSEL_WIDTH * carouselScale,
                    borderWidth: 10,
                    borderColor: "transparent",
                    borderRadius: BUTTON_BORDER_RADIUS * carouselScale,
                    position: "absolute",
                    paddingBottom: CAROUSEL_BOTTOM_HEIGHT_BASE - LARGE_CAROUSEL_BORDER_WIDTH,
                    overflow: "hidden",
                }}
                onPointerDown={clearAutoscrollInterval}
            >
                <Carousel
                    ref={carouselRef}
                    loop={false}
                    dotColor={colors.secondary}
                    dotsOffset={
                        -(LARGE_CAROUSEL_BORDER_WIDTH + CAROUSEL_BOTTOM_HEIGHT_BASE / 2) -
                        (1 - carouselScale) * (CAROUSEL_BOTTOM_HEIGHT_BASE / 2)
                    }
                    style={{ paddingBottom: 0 }}
                    gestureHandlerStyle={{
                        height: "100%",
                        overflow: "hidden",
                    }}
                    canPrev={setCanPrev}
                    canNext={setCanNext}
                    onCarouselChange={onCarouselChange}
                    scrollAnimationDuration={AUTOMATIC_SLIDESHOW_TRANSITION}
                >
                    {steps}
                </Carousel>
                <IconButton
                    size={24 * carouselScale}
                    {...(canPrev
                        ? { iconColor: colors.secondary }
                        : {
                              iconColor: colors.lighterGrey,
                              disabled: true,
                          })}
                    style={{
                        bottom: ARROWS_PADDING * carouselScale,
                        left: ARROWS_PADDING * carouselScale,
                        position: "absolute",
                    }}
                    icon="chevron-left"
                    onPress={onPrev}
                />
                <IconButton
                    size={24 * carouselScale}
                    iconColor={colors.secondary}
                    style={{
                        bottom: ARROWS_PADDING * carouselScale,
                        right: ARROWS_PADDING * carouselScale,
                        position: "absolute",
                    }}
                    icon="chevron-right"
                    onPress={onNext}
                />
            </View>
        </>
    );
};

export const LARGE_CAROUSEL_BORDER_WIDTH = 10;
export const CAROUSEL_BOTTOM_HEIGHT_BASE = 85;

const useStyles = () => {
    const { gridSize, fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                screenTitle: {
                    ...fonts.titleLarge,
                    color: colors.secondary,
                },
                highlightText: {
                    color: colors.secondary,
                },
                screen: {
                    flex: 1,
                    paddingHorizontal: gridSize / 2,
                    // transform: [{ scale: 0.8 }],
                    width: "100%",
                    overflow: "visible",
                },
            }),
        [fonts.titleLarge, colors.secondary, gridSize],
    );
};
